// Version Management Utility for Cache Busting
export const APP_VERSION = '2025.01.08.001';
export const BUILD_TIMESTAMP = Date.now();

export interface DeviceInfo {
  userAgent: string;
  isAndroid: boolean;
  isSamsung: boolean;
  isChrome: boolean;
  version: string;
  timestamp: string;
}

export const getDeviceInfo = (): DeviceInfo => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      userAgent: 'SSR',
      isAndroid: false,
      isSamsung: false,
      isChrome: false,
      version: APP_VERSION,
      timestamp: new Date().toISOString()
    };
  }
  
  const userAgent = navigator.userAgent;
  return {
    userAgent,
    isAndroid: /Android/i.test(userAgent),
    isSamsung: /Samsung/i.test(userAgent),
    isChrome: /Chrome/i.test(userAgent),
    version: APP_VERSION,
    timestamp: new Date().toISOString()
  };
};

export const logVersionInfo = (pageName: string, additionalInfo?: any) => {
  const deviceInfo = getDeviceInfo();
  
  console.log(`🎯 ${pageName} Page Loading:`, {
    ...deviceInfo,
    buildTime: BUILD_TIMESTAMP,
    ...additionalInfo
  });

  // Special logging for Samsung devices
  if (deviceInfo.isSamsung) {
    console.log('📱 Samsung Device Detected - Enhanced Cache Logging:', {
      cacheStrategy: 'aggressive-clear',
      recommendedAction: 'force-refresh-on-version-mismatch'
    });
  }

  return deviceInfo;
};

export const addVersionToUrl = (url: string): string => {
  if (typeof window === 'undefined') return url;
  const urlObj = new URL(url, window.location.origin);
  urlObj.searchParams.set('v', APP_VERSION);
  urlObj.searchParams.set('t', BUILD_TIMESTAMP.toString());
  return urlObj.toString();
};

export const checkVersionMismatch = (): boolean => {
  if (typeof window === 'undefined') return false;
  const storedVersion = localStorage.getItem('app_version');
  return storedVersion !== null && storedVersion !== APP_VERSION;
};

export const updateStoredVersion = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('app_version', APP_VERSION);
  localStorage.setItem('build_timestamp', BUILD_TIMESTAMP.toString());
};

const forceReload = (): void => {
  if (typeof window !== 'undefined' && window.location) {
    window.location.reload();
  }
};

export const handleVersionMismatch = (): void => {
  if (typeof window === 'undefined') return;
  
  if (checkVersionMismatch()) {
    console.log('⚠️ Version mismatch detected, forcing cache clear...');
    
    // Clear caches and force reload
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        Promise.all(cacheNames.map(name => caches.delete(name))).then(() => {
          updateStoredVersion();
          forceReload();
        });
      });
    } else {
      updateStoredVersion();
      forceReload();
    }
  }
};